import { z } from 'zod';
/**
 * Okunan bir barkodun hangi tarafa ait olduğunu belirleyen kural.
 * Eski kodda sabit yazılıydı (`code.startsWith('6') || code.startsWith('CPR')`
 * — rabiekle.dart:548); burada tenant başına yapılandırılır.
 */
export const scanSideRuleSchema = z.object({
    /** Ekranda görünen ad: "HJ Barkodu", "Ürün Kodu". */
    label: z.string(),
    /** Bu taraf olarak tanınacak önekler (büyük/küçük harf duyarsız). */
    prefixes: z.array(z.string()).default([]),
    /** Alternatif tanıma kuralı: regex kaynağı. Önekler eşleşmezse denenir. */
    pattern: z.string().optional(),
});
export const scanRulesSchema = z.object({
    a: scanSideRuleSchema,
    b: scanSideRuleSchema,
});
/**
 * İki kod arasındaki beklenen ilişki.
 *
 * Rabi'de kodlar bağımsızdır: ne okutulursa eşleştirilir (`none`).
 * Brisa'da barkod, topuk kodunun başına "BHJ" eklenmiş hali olmalıdır
 * (`prefix`). Uymayan kayıt yine saklanır ama hatalı işaretlenir — amaç
 * engellemek değil, yanlış etiketlemeyi tespit edip raporlamak.
 */
export const matchRuleSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('none') }),
    z.object({ type: z.literal('prefix'), prefix: z.string().min(1) }),
    z.object({ type: z.literal('equal') }),
    /**
     * Doğruluk bir kuralla değil, yüklenen referans listesiyle belirlenir.
     * Kontrol hem istemcide (anında geri bildirim) hem sunucuda yapılır.
     */
    z.object({ type: z.literal('reference') }),
]);
/**
 * Kategoride hangi ekranların kullanılacağı.
 *
 * Her müşteri aynı işi yapmıyor: Rabi'de gün başında okutulacak barkod
 * listesi var, Brisa'da yok. Kullanılmayan ekranı göstermek operatörü yanıltır.
 */
export const tenantFeaturesSchema = z.object({
    expectedList: z.boolean(),
    imageScan: z.boolean(),
    /** Günlük özet paneli — yalnızca doğrulama yapan kategorilerde anlamlı. */
    dashboard: z.boolean(),
    /**
     * İade akışı: "Ürün kodu yok" düğmesi ve kayıttan sonra sorulan
     * alt kategori seçimi.
     */
    returnFlow: z.boolean().default(false),
    /**
     * Eşleştirmesiz tek kayıt: ürün kodu, görsel ve açıklama alınır.
     * Barkodu olmayan ürünler için.
     */
    singleEntry: z.boolean().default(false),
    /**
     * Toplu gönderi sorgulama: yapıştırılan liste tek seferde sorgulanır.
     *
     * Varsayılan `false` çünkü bu alan sonradan eklendi; veritabanındaki eski
     * kategori satırlarında anahtar yok. Zorunlu yapılsaydı mevcut kayıtlar
     * ayrıştırılamaz, kategori listesi hiç yüklenemezdi.
     */
    bulkQuery: z.boolean().default(false),
});
/** Eşleştirme kategorinin doğrulama kuralına uyuyor mu? */
export function isMatchValid(codeA, codeB, rule) {
    switch (rule.type) {
        case 'prefix':
            return codeA.toUpperCase() === (rule.prefix + codeB).toUpperCase();
        case 'equal':
            return codeA.toUpperCase() === codeB.toUpperCase();
        case 'none':
            return true;
        case 'reference':
            /**
             * Referans listesi bu saf fonksiyona verilmiyor; karar `referansDurumu`
             * ile ayrıca alınıyor. Burada kayda engel olunmaması için doğru
             * kabul ediliyor — asıl işaretleme çağıran tarafta yapılır.
             */
            return true;
    }
}
export function referansDurumu(codeA, codeB, 
/** Anahtarlar büyük harfe çevrilmiş barkod → sipariş kodu. */
referans) {
    const beklenen = referans.get(codeA.trim().toUpperCase());
    if (beklenen === undefined)
        return 'listede-yok';
    return beklenen === codeB.trim().toUpperCase() ? 'dogru' : 'yanlis';
}
/**
 * Eski projede Rabi / Kuzey / Brisa ayrı ayrı kod dosyalarıydı
 * (lib/newpage/rabi, lib/newpage/kuzey, lib/newpage/brisa).
 * Burada hepsi veritabanındaki birer satır. Yeni müşteri = yeni satır.
 */
export const tenantSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    /** Eski `filtre` parametresi: ürün kodunun başlaması gereken önek ('U', '-', ''). */
    codePrefix: z.string(),
    scanRules: scanRulesSchema,
    matchRule: matchRuleSchema,
    features: tenantFeaturesSchema,
    /** Aynı kod birden çok kez kaydedilebilir mi? */
    allowDuplicates: z.boolean(),
    /**
     * İade akışında ürün kodu okutulduktan sonra sorulan seçenekler.
     * Boşsa seçim sorulmaz.
     */
    subCategories: z.array(z.string()).default([]),
    /**
     * Kayıtların doğruluğu hangi kategoriye göre denetlenecek (slug).
     *
     * İade kategorisinde okutulan eşleştirme, asıl kategorideki eşleştirmeyle
     * karşılaştırılır; tutmuyorsa kayıtlar listesinde kırmızı uyarı çıkar.
     * `null` ise karşılaştırma yapılmaz.
     */
    compareWith: z.string().nullable().default(null),
});
/** Kullanıcının bir tenant üzerindeki yetkisi. Sunucu döner, client'a güvenilmez. */
export const tenantAccessSchema = tenantSchema.extend({
    canRead: z.boolean(),
    canWrite: z.boolean(),
});
function matchesRule(code, rule) {
    const upper = code.toUpperCase();
    if (rule.prefixes.some((p) => upper.startsWith(p.toUpperCase()))) {
        return true;
    }
    if (rule.pattern) {
        try {
            return new RegExp(rule.pattern, 'i').test(code);
        }
        catch {
            // Hatalı regex yapılandırması okumayı engellememeli.
            return false;
        }
    }
    return false;
}
/**
 * Okunan barkodun hangi tarafa ait olduğunu belirler.
 *
 * Bu, seri çalışmanın anahtarı: operatör iki barkodu hangi sırayla okutursa
 * okutsun her biri doğru slota oturur, "önce şunu okut" uyarısı çıkmaz.
 * Tanınamayan kod için null döner — çağıran taraf o zaman boş olan slota
 * sıraya göre yerleştirir.
 */
export function detectScanSide(code, rules) {
    const isA = matchesRule(code, rules.a);
    const isB = matchesRule(code, rules.b);
    // Her ikisine de uyuyorsa kural yapılandırması belirsiz; sıraya bırak.
    if (isA && isB)
        return null;
    if (isA)
        return 'a';
    if (isB)
        return 'b';
    return null;
}
function kuralTanimli(rule) {
    return rule.prefixes.length > 0 || !!rule.pattern;
}
/**
 * Bu kategoride kod tanıma kuralı tanımlı mı?
 *
 * Tanımlıysa kurala uymayan kodlar reddedilir. Kamera sürekli açık
 * çalıştığında bu şart: kadrajdan geçen raf etiketi, ambalaj barkodu veya
 * alakasız her kod aksi halde eşleştirmeye girerdi.
 *
 * Kural tanımlı değilse (yeni bir kategori henüz yapılandırılmamışsa)
 * filtreleme yapılmaz — aksi halde hiçbir kod kabul edilmezdi.
 */
export function hasScanRules(rules) {
    return kuralTanimli(rules.a) || kuralTanimli(rules.b);
}
/**
 * Kod bu kategoriye ait mi?
 *
 * Kurallar tanımlıysa yalnızca iki taraftan birine uyan kodlar kabul edilir.
 */
export function isAcceptedCode(code, rules) {
    if (!hasScanRules(rules))
        return true;
    return matchesRule(code, rules.a) || matchesRule(code, rules.b);
}
//# sourceMappingURL=tenant.js.map